'use client';

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { acquirePhysicalWrite, getPhysicalWrite, releasePhysicalWrite, subscribePhysicalWrite, SCHEDULE_WRITE_SCOPE } from '../hooks/physicalWriteSlot';

export interface SelectedScheduleSaveIdentity {
  ownerId: string;
  scheduleId: string;
  routeKey: string;
  term: string;
}

export interface SelectedScheduleSaveOptions {
  ownerId: string | null;
  scheduleId: string | null;
  routeKey: string;
  term: string;
  draft: unknown;
  classes: readonly string[];
  isBlocked?: () => boolean;
  writeClasses: (scheduleId: string, classes: string[]) => Promise<unknown>;
  onCommit: (identity: SelectedScheduleSaveIdentity, classes: string[]) => void;
  onError?: (error: unknown) => void;
}

// Match the semester-write contract: a dispatched write retains its physical
// lock until settlement, even across context replacement or remounts.


export function useSelectedScheduleSave(options: SelectedScheduleSaveOptions) {
  const { ownerId, scheduleId, routeKey, term, draft } = options;
  const context = useMemo(() => ({ ownerId, scheduleId, routeKey, term }), [ownerId, scheduleId, routeKey, term]);
  const committed = useRef<{ context: typeof context; options: SelectedScheduleSaveOptions } | null>(null);
  const lifetime = useRef<symbol | null>(null);
  const [state, setState] = useState<{ isPending: boolean; error: unknown }>(() => ({ isPending: getPhysicalWrite(SCHEDULE_WRITE_SCOPE) !== undefined, error: null }));

  useLayoutEffect(() => { committed.current = { context, options }; });
  useLayoutEffect(() => {
    const epoch = Symbol('selected-schedule-save');
    lifetime.current = epoch;
    setState({ isPending: getPhysicalWrite(SCHEDULE_WRITE_SCOPE) !== undefined, error: null });
    return () => { if (lifetime.current === epoch) lifetime.current = null; };
  }, [context]);
  useLayoutEffect(() => {
    const update = () => {
      if (lifetime.current !== null) setState(previous => ({ ...previous, isPending: getPhysicalWrite(SCHEDULE_WRITE_SCOPE) !== undefined }));
    };
    const unsubscribe = subscribePhysicalWrite(SCHEDULE_WRITE_SCOPE, update);
    update();
    return unsubscribe;
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    const current = committed.current;
    const epoch = lifetime.current;
    if (!current || !epoch || current.context !== context || current.options.draft !== draft
      || !context.ownerId || !context.scheduleId || getPhysicalWrite(SCHEDULE_WRITE_SCOPE) !== undefined || current.options.isBlocked?.()) return false;
    const captured = current.options;
    if (!Array.isArray(captured.classes) || !captured.classes.every(value => typeof value === 'string')) return false;
    const classes = [...captured.classes];
    const identity: SelectedScheduleSaveIdentity = {
      ownerId: context.ownerId, scheduleId: context.scheduleId, routeKey: context.routeKey, term: context.term,
    };
    const operation = Symbol('selected-schedule-write');
    if (!acquirePhysicalWrite(SCHEDULE_WRITE_SCOPE, operation)) return false;
    setState({ isPending: true, error: null });
    const isCurrent = () => {
      const latest = committed.current;
      return lifetime.current === epoch && latest?.context === context && getPhysicalWrite(SCHEDULE_WRITE_SCOPE) === operation
        && latest.options.draft === draft && latest.options.classes.length === classes.length
        && latest.options.classes.every((value, index) => value === classes[index]);
    };
    try {
      // Keep the receipt snapshot private even if a transport mutates its input.
      await captured.writeClasses(identity.scheduleId, [...classes]);
      if (!isCurrent()) return false;
      committed.current!.options.onCommit(identity, [...classes]);
      return true;
    } catch (error) {
      if (isCurrent()) {
        setState(previous => ({ ...previous, error }));
        committed.current!.options.onError?.(error);
      }
      return false;
    } finally {
      releasePhysicalWrite(SCHEDULE_WRITE_SCOPE, operation);
    }
  }, [context, draft]);

  const isPhysicallyPending = useCallback(() => getPhysicalWrite(SCHEDULE_WRITE_SCOPE) !== undefined, []);
  return { save, isPhysicallyPending, ...state };
}
